/**
 * Fetch Utility with Vercel Deployment Protection Bypass
 *
 * Automatically adds x-vercel-protection-bypass parameter to API requests
 * when NEXT_PUBLIC_VERCEL_BYPASS_SECRET is set (Preview environment).
 *
 * Also handles 401 Unauthorized errors with intelligent session expiration detection.
 */

// Track consecutive 401 errors to distinguish real session expiration from temporary navigation issues
let consecutive401Count = 0;
let sessionExpiredShown = false;
let debounceTimer: NodeJS.Timeout | null = null;

// Only show notification after 3 consecutive 401s (prevents false positives during navigation)
const CONSECUTIVE_401_THRESHOLD = 3;
const DEBOUNCE_DELAY = 1000; // Wait 1 second before showing notification

/**
 * Add Vercel bypass parameter to URL if secret is available
 */
function addBypassParam(url: string): string {
  const bypassSecret = process.env.NEXT_PUBLIC_VERCEL_BYPASS_SECRET;

  if (!bypassSecret) {
    return url;
  }

  // Safety check: only run on client-side
  if (typeof window === 'undefined') {
    // On server-side, return URL as-is (no bypass needed)
    return url;
  }

  const urlObj = new URL(url, window.location.origin);
  urlObj.searchParams.set('x-vercel-protection-bypass', bypassSecret);

  return urlObj.toString();
}

/**
 * Show session expired notification (only after multiple consecutive 401s)
 */
function showSessionExpiredNotification() {
  // Safety check: only run on client-side
  if (typeof window === 'undefined') return;

  if (sessionExpiredShown) return;

  consecutive401Count++;

  // Clear previous debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  // Only show notification after threshold consecutive 401s
  if (consecutive401Count >= CONSECUTIVE_401_THRESHOLD) {
    // Debounce to avoid showing during rapid page transitions
    debounceTimer = setTimeout(() => {
      if (consecutive401Count >= CONSECUTIVE_401_THRESHOLD && !sessionExpiredShown) {
        sessionExpiredShown = true;
        window.dispatchEvent(new CustomEvent('session-expired'));
      }
    }, DEBOUNCE_DELAY);
  }
}

/**
 * Reset 401 counter when a request succeeds
 */
function resetSessionCheck() {
  consecutive401Count = 0;
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}

/**
 * Enhanced fetch that automatically adds Vercel bypass parameter
 * and handles 401 Unauthorized errors with intelligent detection
 *
 * Use this instead of native fetch() for all API calls
 */
export async function fetchWithBypass(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const url =
    typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
  const bypassUrl = addBypassParam(url);

  let response: Response;

  if (typeof input === 'string') {
    response = await fetch(bypassUrl, init);
  } else if (input instanceof URL) {
    response = await fetch(bypassUrl, init);
  } else {
    response = await fetch(new Request(bypassUrl, input), init);
  }

  // Handle 401 Unauthorized - session expired
  if (response.status === 401) {
    showSessionExpiredNotification();
  } else if (response.ok) {
    // Reset counter on successful requests (2xx status codes)
    resetSessionCheck();
  }

  return response;
}
